import { Component, type ErrorInfo, type ReactNode } from "react";
import { APP_VERSION } from "@/lib/version";

/* ============================================================
   A rede que impede a tela branca

   Um erro em qualquer render derruba o React inteiro, e o que sobra é
   uma página em branco: sem texto, sem botão, sem pista. Num app de
   loja isso é a pior tela possível — a pessoa desinstala e escreve uma
   estrela, e a gente nunca fica sabendo o que quebrou.

   DUAS COISAS DIFERENTES CAEM AQUI, e elas pedem respostas opostas.

   1. PEDAÇO QUE SUMIU — e este se conserta sozinho, em silêncio.

   O app carrega o mapa sob demanda (`await import("leaflet")`), e o
   build dá nome com hash aos pedaços. O Hosting serve só a versão
   atual: no deploy seguinte aquele arquivo deixa de existir. Quem
   deixou o app aberto desde o almoço, ao tocar em "escolher lugar" à
   noite, pede um arquivo que não está mais lá.

   A resposta certa NÃO é avisar que a versão está velha. Num app
   nativo o sistema atualiza sozinho e a pessoa nunca sabe que existiu
   uma versão; falar nisso empurra pra ela um problema que é nosso, e
   ela não teria o que fazer com a informação. Então: recarrega uma vez,
   calado. Ela vê um piscar e a tela abre.

   A trava de uma vez só é obrigatória. Sem ela, um erro que sobrevive
   ao recarregamento vira um laço infinito de telas piscando — que é
   pior que a tela branca, porque nem dá pra ler o que aconteceu.

   2. ERRO DE VERDADE — e este precisa aparecer.

   Recarregar não conserta bug de código, e tentar esconder faz a pessoa
   repetir o caminho até desistir. Aqui a tela diz o que dá pra dizer e
   oferece a única ação que às vezes resolve.
   ============================================================ */

const TENTOU = "nhomi.recarregou";

/**
 * O erro é de pedaço que não existe mais?
 *
 * A mensagem varia por navegador — cada um escreve a sua —, então a
 * checagem olha por pedaços de texto em vez de comparar exato. Errar pro
 * lado de recarregar é barato; errar pro lado de não recarregar deixa a
 * pessoa com a função quebrada.
 */
function ehPedacoQueSumiu(err: unknown): boolean {
  const m = String((err as Error)?.message ?? err ?? "").toLowerCase();
  return m.includes("dynamically imported module")
    || m.includes("failed to fetch dynamically")
    || m.includes("importing a module script failed")
    || m.includes("error loading dynamically imported module");
}

/** Recarrega no máximo uma vez por sessão. Devolve se chegou a tentar. */
function recarregarUmaVez(): boolean {
  try {
    if (sessionStorage.getItem(TENTOU)) return false;
    sessionStorage.setItem(TENTOU, "1");
  } catch {
    // Sem armazenamento não dá pra travar o laço, e um laço é pior que
    // um erro visível. Então não recarrega.
    return false;
  }
  window.location.reload();
  return true;
}

/* O Vite avisa quando um pedaço não carrega, ANTES de o erro chegar no
   render. Pegar aqui conserta sem a tela nem piscar de erro. */
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    recarregarUmaVez();
  });
}

interface Estado { quebrou: boolean; detalhe: string }

export class Rede extends Component<{ children: ReactNode }, Estado> {
  override state: Estado = { quebrou: false, detalhe: "" };

  static getDerivedStateFromError(err: unknown): Estado {
    if (ehPedacoQueSumiu(err) && recarregarUmaVez()) {
      // Recarregando: não desenha nada, a página já está indo embora.
      return { quebrou: false, detalhe: "" };
    }
    return { quebrou: true, detalhe: String((err as Error)?.message ?? err ?? "") };
  }

  override componentDidCatch(err: Error, info: ErrorInfo): void {
    // Por enquanto só o console. O relatório que chega até nós é o
    // próximo passo — sem ele, isto aqui só ajuda quem estiver com o
    // celular na mão e o cabo no computador.
    console.error("[nhomi] tela quebrou", { err, info, versao: APP_VERSION });
  }

  override render(): ReactNode {
    if (!this.state.quebrou) return this.props.children;

    return (
      <div className="quebrou">
        <div className="quebrou-caixa">
          <div className="quebrou-emoji" aria-hidden="true">🍳</div>
          <h1>Essa tela queimou</h1>
          <p>
            Alguma coisa quebrou aqui do nosso lado — não foi você. Tentar
            de novo costuma resolver.
          </p>
          <button className="btn btn-primary"
            onClick={() => { window.location.href = "/"; }}>
            Voltar pro começo
          </button>
          {/* A versão aparece porque um print vale mais que uma
              descrição, e sem ela a resposta pra "qual versão você está
              usando?" nunca vem certa num app que atualiza sozinho. */}
          <p className="quebrou-versao">versão {APP_VERSION}</p>
        </div>
      </div>
    );
  }
}
